import { Box, Button, MobileStepper, SelectChangeEvent, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { FormikTouched, useFormik } from 'formik';
import * as Yup from 'yup';
import { useSearchParams } from 'react-router-dom';
import EmailQuestionnaireService from '../../shared-services/email-questionnaire/email-questionnaire.service';
import { IRecieveEmailQuestionniare } from '../../shared-services/email-questionnaire/email-questionnaire.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { KeyboardArrowLeft, KeyboardArrowRight, SaveOutlined } from '@mui/icons-material';
import gaiaTheme from '../../../../root-components/theme';
import EmailQuestionnaireAnswerService from '../../shared-services/email-questionnaire-answer/email-questionnaire-answer.service';
import { IPostEmailQuestionnaireAnswer } from '../../shared-services/email-questionnaire-answer/email-questionnaire-answer.model';
import { LoadingPage } from '../../../../components/pages/loading-page';
import { EmailCampaignEmployeeNameAndEmail } from './email-campaign-employee-name-and-email';
import { EmailCampaignEmployeeMethodCommuting } from './email-campaign-employee-method-commuting';
import { EmaiLCampaignEmployeeDaysWorking } from './email-campaign-employee-days-working';
import { IMeasurement } from '../../../../shared/services/category/category.model';
import { Message } from '../../../../components/presentation-components/miscellaneous-components/message';
import { LoadingButton } from '@mui/lab';

export const EmailCampaignEmployeeCommuteForm: FunctionComponent<{
  isDemonstrationPage?: boolean
  includeProjects?: boolean,
}> = ({
  isDemonstrationPage = false,
  includeProjects,
}) => {
    const theme = useTheme();
    const emailQuestionnaireService = new EmailQuestionnaireService();
    const emailQuestionnaireAnswerService = new EmailQuestionnaireAnswerService()
    const userNotificationsContext = useContext(UserNotificationsContext);
    const [storedToken, setStoredToken] = useState<string>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [emailQuestionnaireDetails, setEmailQuestionnaireDetails] = useState<IRecieveEmailQuestionniare>()
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>();
    const [backgroundColor, setBackgroundColor] = useState<string>('common.white')
    const [activeCategoryName, setActiveCategoryName] = useState<string>('')
    const [success, setSuccess] = useState<boolean>(false)
    const [error, setError] = useState<string>()
    const [isTest, setIsTest] = useState<boolean>(false);
    const [measurementFilters, setMeasurementFilters] = useState<IMeasurement[] | undefined>()
    const [isPageReady, setIsPageReady] = useState<boolean>(false)
    const [selectedData, setSelectedData] = useState<any>({
      detailLevelTwo: '',
      detailLevelThree: '',
      detailLevelFour: '',
      detailLevelFive: ''
    })
    const [filterData, setFilterData] = useState<any>({
      detailLevelTwo: [],
      detailLevelThree: [],
      detailLevelFour: [],
      detailLevelFive: []
    })
    const categoryId = { buisnessTravel: 'f1106601-0f5f-4416-b2cd-67f9d9cbc633', employeeCommutingZeroEmissions: '3bf59556-d75f-4fe7-b015-eedf90efefce' }
    const [activeStep, setActiveStep] = React.useState(0);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const nameSchema = Yup.object().shape({
      name: Yup.string().required('Your name is required'),
      emailAddress: Yup.string().email('Email address invalid').required('Your email address is required'),
    });
    const methodCommutingSchema = Yup.object().shape({
      detailLevelTwo: Yup.string(),
      detailLevelThree: Yup.string(),
      detailLevelFour: Yup.string(),
      detailLevelFive: Yup.string(),
      measurementUnitId: Yup.string().required('You must provide units'),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
    });
    const daysWorkingSchema = Yup.object().shape({
      numberHomeWorkingDays: Yup.number().required('Number of working days is required').min(0).max(7, 'The maximum number of days you can work from home a week is 7'),
      numberCommutingDays: Yup.number().required('Number of commuting days is required').min(0).max(7, 'The maximum number of days you can commute to work a week is 7'),
      locationId: Yup.string().required('Location is required'),
    });

    const formik = useFormik<any>({
      initialValues: {
        name: undefined,
        emailAddress: undefined,
        numberHomeWorkingDays: undefined,
        numberCommutingDays: undefined,
        locationId: '',
        projectId: null,
        detailLevelTwo: undefined,
        detailLevelThree: undefined,
        detailLevelFour: undefined,
        detailLevelFive: undefined,
        unitsConsumed: undefined,
        measurementUnitId: '',
      },
      validationSchema: !isTest && !isDemonstrationPage && emailQuestionnaireDetails && !emailQuestionnaireDetails.isGenericTeamEmail ?
        activeStep === 0 ? methodCommutingSchema : daysWorkingSchema :
        !isTest && !isDemonstrationPage && emailQuestionnaireDetails && emailQuestionnaireDetails.isGenericTeamEmail ?
          activeStep === 0 ? nameSchema :
            activeStep === 1 ? methodCommutingSchema : daysWorkingSchema :
          undefined,
      onSubmit: (values: IPostEmailQuestionnaireAnswer) => {
        values.reportPeriodId = emailQuestionnaireDetails?.reportPeriodId!;
        values.detailLevelTwo = selectedData.detailLevelTwo;
        if (!emailQuestionnaireDetails?.isReportPeriodOpen) {
          userNotificationsContext.openDialog({
            active: true, title: 'Action Not Allowed',
            message: `Unable to save your response because this survey is now closed`,
            buttonCopy: 'Ok',
            hideCancelButton: true
          })
        } else {
          if (!isSubmitting) {
            if (values.projectId === '') {
              values.projectId = null
            }
            setIsSubmitting(true)
            emailQuestionnaireAnswerService.saveAnswers(storedToken!, values)
              .then(response => {
                setSuccess(true)
                setIsSubmitting(false)
              })
              .catch(err => {
                console.log(err)
                setIsSubmitting(false)
                if (err.response.data.code === 1) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                } else if (err.response.data.code === 2) {
                  userNotificationsContext.openDialog({
                    active: true, title: 'Action Not Allowed',
                    message: `Unable to save your response because you have already completed this survey`,
                    buttonCopy: 'Ok',
                    hideCancelButton: true
                  })
                } else {
                  userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
                }
              })
          }
        }
      }
    });

    useEffect(() => {
      if (searchParams.has('token')) {
        const token = searchParams.get('token')
        setStoredToken(`${token}`);
        if (token && token !== 'TEST-EMAIL') {
          setIsPageReady(false)
          emailQuestionnaireService.getEmailQuestionnaire(`${token}`)
            .then(response => {
              setEmailQuestionnaireDetails(response);
              setIsPageReady(true)
            })
            .catch(err => {
              console.log(err)
              setIsPageReady(true)
              setError(err.response.data.message)
            })
        } else if (token === 'TEST-EMAIL') {
          setIsTest(true);
          setIsPageReady(true)
        }
      }
      if (isDemonstrationPage) {
        setIsPageReady(true)
      }
    }, [])

    useEffect(() => {
      if (emailQuestionnaireDetails && !emailQuestionnaireDetails.isGenericTeamEmail) {
        formik.setFieldValue('name', emailQuestionnaireDetails.name)
        formik.setFieldValue('emailAddress', emailQuestionnaireDetails.emailAddress)
      }
    }, [emailQuestionnaireDetails])


    useEffect(() => {
      setFilterData({
        detailLevelTwo: [],
        detailLevelThree: [],
        detailLevelFour: [],
        detailLevelFive: []
      })
      setMeasurementFilters(undefined);
    }, [activeCategoryName])


    useEffect(() => {
      if (emailQuestionnaireDetails && selectedData.detailLevelTwo)
        getData()
    }, [selectedData])

    const selectHandler = (method: string) => {
      setBackgroundColor('secondary.light');
      setActiveCategoryName(method)

      if (!isDemonstrationPage) {
        if (method === "Walk") {
          setSelectedCategoryId(categoryId.employeeCommutingZeroEmissions)
          setSelectedData({ detailLevelTwo: 'Walk' })
        }

        if (method === "Cycle") {
          setSelectedCategoryId(categoryId.employeeCommutingZeroEmissions)
          setSelectedData({ detailLevelTwo: 'Cycle' })
        }

        if (method === "Other") {
          setSelectedCategoryId(categoryId.employeeCommutingZeroEmissions)
          setSelectedData({ detailLevelTwo: 'Other' })
        }

        if (method === "Motorbike") {
          setSelectedCategoryId(categoryId.buisnessTravel)
          setSelectedData({ detailLevelTwo: 'Motorbike' })
        }

        if (method === "Car") {
          setSelectedCategoryId(categoryId.buisnessTravel)
          setSelectedData({ detailLevelTwo: 'Cars (by size)' })
        }

        if (method === "Taxi") {
          setSelectedCategoryId(categoryId.buisnessTravel)
          setSelectedData({ detailLevelTwo: 'Taxis' })
        }

        if (method === "Bus") {
          setSelectedCategoryId(categoryId.buisnessTravel)
          setSelectedData({ detailLevelTwo: 'Bus' })
        }

        if (method === "Rail") {
          setSelectedCategoryId(categoryId.buisnessTravel)
          setSelectedData({ detailLevelTwo: 'Rail' })
        }
      }
    }

    const handleFilterSelect = (e: SelectChangeEvent<string>) => {
      setMeasurementFilters(undefined);
      const resetData: any = {}
      if (e.target.name === 'detailLevelTwo') {
        resetData.detailLevelThree = '';
        resetData.detailLevelFour = '';
        resetData.detailLevelFive = '';
      };

      if (e.target.name === 'detailLevelThree') {
        resetData.detailLevelFour = '';
        resetData.detailLevelFive = '';
      };

      if (e.target.name === 'detailLevelFour') {
        resetData.detailLevelFive = '';
      };
      setSelectedData({ ...selectedData, ...resetData, [e.target.name]: [e.target.value] })
      setFilterData({ ...filterData, ...resetData })
      formik.setValues({ ...formik.values, [e.target.name]: e.target.value });
    }

    const getData = () => {
      if (searchParams.has('token')) {
        const token = searchParams.get('token')
        emailQuestionnaireService.filterData(selectedCategoryId!, getQueryString(selectedData, `${token}`))
          .then(response => {
            setMeasurementFilters(response.measurementUnits)
            if (response.measurementUnits?.length === 1) {
              formik.setFieldValue('measurementUnitId', response.measurementUnits[0].id);
            }
            delete response.measurementUnits
            setFilterData(response)
          })
      }
    }

    function getQueryString(data: any, token: string): string {
      let queryString = `?token=${token}&reportPeriodId=${emailQuestionnaireDetails?.reportPeriodId}`
      for (const [key, value] of Object.entries(data)) {

        if (queryString !== '?') {
          queryString += '&'
        };

        if (value) {
          queryString += `${key}=${value}`;
        } else {
          queryString = queryString.substring(0, queryString.length - 1);
        }
      };
      return queryString;
    }


    const handleNext = async () => {
      const errors = await formik.validateForm();
      const currentErrors = Object.keys(errors).filter((key) => {
        if (emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 0) {
          return key === 'name' || key === 'emailAddress';
        } else if ((!emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 0) ||
          (emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 1)) {
          return key === 'measurementUnitId' || key === 'unitsConsumed';
        } else if ((!emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 1) ||
          (emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 2)) {
          return (
            key === 'numberHomeWorkingDays' ||
            key === 'numberCommutingDays' ||
            key === 'locationId'
          );
        }
        return false;
      });
      const hasErrors = currentErrors.length > 0;
      if (!hasErrors) {
        if (emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 0) {
          emailQuestionnaireAnswerService.checkIfCompleted(storedToken!, { emailAddress: formik.values.emailAddress })
            .then(response => {
              setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
            })
            .catch(err => {
              console.log(err)
              setError(err.response.data.message)
            })
        } else {
          setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
        }
      } else {
        const touchedFields: FormikTouched<IPostEmailQuestionnaireAnswer> = currentErrors.reduce((acc, key) => {
          acc[key as keyof IPostEmailQuestionnaireAnswer] = true;
          return acc;
        }, {} as FormikTouched<IPostEmailQuestionnaireAnswer>);
        formik.setTouched(touchedFields);
      }
    };


    const handleBack = () => {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    return (
      <LoadingPage pageReady={isPageReady}>
        {error &&
          <Message
            status={'Action Not Allowed'}
            message={error}
          />}
        {/* You have completed this survey!
        Your response has already been saved successfully. Thank you for taking the time to complete the survey. */}
        {emailQuestionnaireDetails && !emailQuestionnaireDetails?.isReportPeriodOpen &&
          <Message
            status={'Action Not Allowed'}
            message={'This survey is no longer open for completion.'}
          />}
        {success &&
          <Message
            status={'Successful'}
            message={'Your response has been saved successfully. Thank you for taking the time to complete the survey.'}
          />}
        {!success && !error && (isDemonstrationPage || isTest || (!isDemonstrationPage && emailQuestionnaireDetails?.isReportPeriodOpen)) &&
          <Box>
            <Stack display='flex' alignItems={isMobileModeMd ? 'flex-start' : 'flex-end'} flexDirection={isMobileModeMd ? 'column' : 'row'} justifyContent='start'>
              <Typography variant="h5" sx={{ fontWeight: 600, mr: 2, mt: 2 }}>
                Employee Survey
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: isMobileModeMd ? 1 : 0 }}>
                Average time to complete this survey is 2 minutes
              </Typography>
            </Stack>
            <Box
              sx={{
                width: '100%',
                ml: 0
              }} >
              {emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 0 &&
                <EmailCampaignEmployeeNameAndEmail formik={formik}
                />}
              {((emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 1) || (!emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 0)) &&
                <EmailCampaignEmployeeMethodCommuting
                  formik={formik}
                  isDemonstrationPage={isDemonstrationPage}
                  isTest={isTest}
                  measurementFilters={measurementFilters}
                  selectedData={selectedData}
                  filterData={filterData}
                  activeCategoryName={activeCategoryName}
                  backgroundColor={backgroundColor}
                  selectHandler={selectHandler}
                  handleFilterSelect={handleFilterSelect}
                />}
              {((emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 2) || (!emailQuestionnaireDetails?.isGenericTeamEmail && activeStep === 1)) &&
                <EmaiLCampaignEmployeeDaysWorking
                  formik={formik}
                  includeProjects={includeProjects}
                  isDemonstrationPage={isDemonstrationPage}
                  isTest={isTest}
                  emailQuestionnaireDetails={emailQuestionnaireDetails}
                />}
              <MobileStepper
                sx={{ flexGrow: 1 }}
                variant="dots"
                steps={emailQuestionnaireDetails && emailQuestionnaireDetails?.isGenericTeamEmail ? 3 : 2}
                position="static"
                activeStep={activeStep}
                nextButton={(!emailQuestionnaireDetails?.isGenericTeamEmail && activeStep !== 1) || (emailQuestionnaireDetails?.isGenericTeamEmail && activeStep !== 2) ?
                  <Button onClick={handleNext}>
                    <Typography> Next</Typography>
                    {theme.direction === 'rtl' ? (
                      <KeyboardArrowLeft />
                    ) : (
                      <KeyboardArrowRight />
                    )}
                  </Button> :
                  <LoadingButton
                    onClick={formik.submitForm}
                    disabled={isSubmitting}
                    startIcon={<SaveOutlined />}
                    variant='outlined'
                    loading={isSubmitting || isDemonstrationPage || isTest}
                    loadingPosition="start">
                    <Typography>{isSubmitting ? 'Saving' : 'Save'}</Typography>
                  </LoadingButton>
                }
                backButton={
                  <Button onClick={handleBack} disabled={activeStep === 0 || isSubmitting}>
                    {theme.direction === 'rtl' ? (
                      <KeyboardArrowRight />
                    ) : (
                      <KeyboardArrowLeft />
                    )}
                    Back
                  </Button>
                }
              />
            </Box >
          </Box >}
      </LoadingPage>
    )
  }